import { useEffect, useState } from 'react';

/**
 * Milliseconds remaining until the next midnight in `timeZone`.
 *
 * Reading the wall-clock time through Intl rather than the local Date methods
 * means the countdown lands on midnight in Paris for every visitor, whatever
 * their device timezone is.
 */
function msUntilMidnight(timeZone, now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(now);

  // en-GB renders midnight as "24:00:00"; normalise it to 0.
  const [hours, minutes, seconds] = parts.split(':').map(Number);
  const elapsed = (((hours % 24) * 60 + minutes) * 60 + seconds) * 1000;

  return 86_400_000 - elapsed;
}

function split(ms) {
  const clamped = Math.max(0, ms);
  return {
    hours: Math.floor(clamped / 3_600_000),
    minutes: Math.floor((clamped / 60_000) % 60),
    seconds: Math.floor((clamped / 1000) % 60),
    total: clamped,
  };
}

/**
 * Countdown to the next midnight, rolling over automatically.
 *
 * The offer window restarts each day, so rather than counting down to a fixed
 * date (which silently expires — as the World Cup banner did), this derives the
 * remaining time from the clock on every tick and never needs resetting.
 */
export default function useDailyCountdown(timeZone = 'Europe/Paris') {
  const [timeLeft, setTimeLeft] = useState(() => split(msUntilMidnight(timeZone)));

  useEffect(() => {
    const tick = () => setTimeLeft(split(msUntilMidnight(timeZone)));

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [timeZone]);

  return timeLeft;
}
